import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router';
import { LifeBuoy } from 'lucide-react';

import { DataTable, type DataTableColumn } from '@/components/admin/data-table';
import { FilterChips } from '@/components/admin/filter-chips';
import { EmptyState } from '@/components/ui/empty-state';
import {
  ALL_SUPPORT_TICKET_STATUSES,
  SUPPORT_TICKET_STATUS,
  requesterDisplayName,
  type SupportTicketStatus,
  type SupportTicketSummary,
} from '@/contracts';
import { useLocale, useTranslations } from '@/i18n/use-locale';
import { formatCount } from '@/lib/format';
import { relativeTime } from '@/lib/format-time';
import { useAccountKindLabel, useSupportStatusLabel } from '@/lib/labels';
import { useSupportTicketsQuery } from '@/lib/queries';
import { QueryGate } from '@/components/ui/query-gate';
import { Card } from '@/components/ui/card';
import { PageHeader } from '@/components/admin/page-header';

/**
 * The support inbox: what people wrote in from the app, one status at a time.
 *
 * The tabs are the server's `?status=` filter rather than a client-side split, and
 * the list is cursor-paginated, so what is on screen is always the API's answer for
 * that tab and nothing is counted or sorted here. The cursor is bound to the status it
 * was issued under, and the status is part of the query key, so changing tabs starts
 * a fresh first page rather than sending another tab's cursor. The only number shown
 * is the active tab's `meta.total`, which the API puts on the first page alone; the
 * other tabs carry none, because the console has no count for them.
 *
 * `open` first, because it is the tab with work in it: a ticket is `open` until the
 * team replies, and the whole reason this screen exists is that somebody is waiting.
 */
export function SupportRoute() {
  const t = useTranslations('admin.support');
  const statusLabel = useSupportStatusLabel();
  const kindLabel = useAccountKindLabel();
  const { locale } = useLocale();
  const [status, setStatus] = useState<SupportTicketStatus>(SUPPORT_TICKET_STATUS.OPEN);

  const tickets = useSupportTicketsQuery(status);

  const columns: ReadonlyArray<DataTableColumn<SupportTicketSummary>> = useMemo(
    () => [
      {
        key: 'subject',
        header: t('table.subject'),
        cell: (row) => (
          <Link
            to={`/support/${row.id}`}
            className="font-semibold text-navy transition hover:text-pitch hover:underline"
          >
            {row.subject}
          </Link>
        ),
      },
      {
        key: 'requester',
        header: t('table.requester'),
        cell: (row) => (
          <>
            <p className="font-semibold text-navy">
              {requesterDisplayName(row.requester) ?? t('unnamedRequester')}
            </p>
            <p className="mt-0.5 text-[11.5px] text-conc-500">
              {kindLabel(row.requester.accountKind)}
              {row.requester.organizationName && ` · ${row.requester.organizationName}`}
            </p>
          </>
        ),
      },
      {
        key: 'messages',
        header: t('table.messages'),
        cellClassName: 'num text-conc-600',
        cell: (row) => formatCount(row.messageCount),
      },
      {
        key: 'lastMessage',
        header: t('table.lastMessage'),
        cell: (row) => (
          <time dateTime={row.lastMessageAt} className="num text-conc-600">
            {relativeTime(new Date(row.lastMessageAt), locale)}
          </time>
        ),
      },
    ],
    [t, kindLabel, locale],
  );

  // A failed first page is the whole inbox failing, which gets the retry panel. A
  // failed *next* page keeps what loaded; the "load more" control is its retry.
  return (
    <QueryGate query={tickets}>
      {({ pages }) => {
        const rows = pages.flatMap((page) => page.items);
        // The API sends the status's total on the first page only; the tab shows it when it came.
        const activeTotal = pages[0]?.meta.total;

        return (
          <div className="space-y-6">
            <PageHeader title={t('heading')} sub={t('subheading')} />

            <FilterChips<SupportTicketStatus>
              label={t('filter.label')}
              value={status}
              onChange={setStatus}
              options={ALL_SUPPORT_TICKET_STATUSES.map((value) => ({
                value,
                label: statusLabel(value),
                ...(value === status && activeTotal !== undefined ? { count: activeTotal } : {}),
              }))}
            />

            <Card className="overflow-hidden">
              <DataTable
                rows={rows}
                columns={columns}
                rowKey={(row) => row.id}
                caption={t('table.caption')}
                empty={
                  <EmptyState
                    icon={LifeBuoy}
                    title={t(`empty.${status}.title`)}
                    hint={t(`empty.${status}.hint`)}
                  />
                }
                footer={
                  tickets.hasNextPage ? (
                    <div className="border-t border-conc-100 px-5 py-3 text-center">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={tickets.isFetchingNextPage}
                        onClick={() => void tickets.fetchNextPage()}
                        className="py-2"
                      >
                        {tickets.isFetchingNextPage ? t('loadingMore') : t('loadMore')}
                      </Button>
                    </div>
                  ) : null
                }
              />
            </Card>
          </div>
        );
      }}
    </QueryGate>
  );
}
